import { Badge, IconButton, Tooltip } from '@orbitalwatch/design';
import type { ReactNode } from 'react';

export interface RightRailToggle {
    icon: ReactNode;
    /** Accessible name and tooltip ("Table of contents", "Comments"). */
    label: string;
    /** The panel it opens is open (accent-soft fill, `aria-pressed`). */
    active: boolean;
    onToggle: () => void;
    /** Count pill in the corner (comments); hidden at 0. */
    badge?: number;
    /** Not rendered: toggles the caller may not use stay hidden. */
    hidden?: boolean;
    disabled?: boolean;
}

export interface RightRailProps {
    toggles: RightRailToggle[];
    /** Accessible name of the toolbar. Default "Page panels". */
    label?: string;
    className?: string;
}

/**
 * DS gap: the reader's narrow right rail of panel toggles (TOC, comments). The library `Rail` is the left section rail
 * (links with labels); this is a vertical toolbar of library `IconButton`s with tooltips (`ds.css` "right rail").
 */
export function RightRail(props: RightRailProps) {
    const toggles = props.toggles.filter(toggle => !toggle.hidden);
    if (toggles.length === 0) return null;
    return (
        <div className={['ark-right-rail', props.className].filter(Boolean).join(' ')} role="toolbar" aria-orientation="vertical" aria-label={props.label ?? 'Page panels'}>
            {toggles.map(toggle => (
                <span className="ark-right-rail-item" key={toggle.label}>
                    <Tooltip content={toggle.label} placement="left">
                        <IconButton
                            icon={toggle.icon}
                            aria-label={toggle.label}
                            isActive={toggle.active}
                            isDisabled={toggle.disabled}
                            onClick={() => toggle.onToggle()}
                        />
                    </Tooltip>
                    {toggle.badge ? <Badge className="ark-right-rail-badge" count={toggle.badge} size="sm" tone="neutral" aria-label={`${toggle.badge} ${toggle.label.toLowerCase()}`} /> : null}
                </span>
            ))}
        </div>
    );
}
